/* 55carat original photographic ring showcase. No dependencies or 3D model. */
(() => {
'use strict';
const scriptBase = new URL('.', document.currentScript?.src || location.href);
const stones = ['sapphire', 'emerald', 'ruby', 'yellow'];
const names = {sapphire:'Blue sapphire', emerald:'Emerald', ruby:'Ruby', yellow:'Yellow sapphire'};
class CaratRing extends HTMLElement {
  static get observedAttributes() {
    return ['stone', 'speed', 'paused', 'cycle', 'dwell', 'asset-base', ...stones.map(s => 'src-' + s)];
  }
  connectedCallback() {
    if (this.active) return;
    this.active = true;
    this.token = (this.token || 0) + 1;
    this.lifecycle = (this.lifecycle || 0) + 1;
    this.cache = new Map();
    this.root = this.shadowRoot || this.attachShadow({mode:'open'});
    this.root.innerHTML = `<style>
:host{display:block;width:100%;height:100%;min-height:260px;position:relative;contain:layout paint;--duration:28s;--motion:paused;--rx:0deg;--ry:0deg;--px:0px;--py:0px}
.stage{position:absolute;inset:4%;display:grid;place-items:center;perspective:1000px}
.reveal{position:relative;width:100%;height:100%;opacity:0;transform:translateY(18px) scale(.94);transition:opacity 1s ease,transform 1.4s cubic-bezier(.2,.7,.2,1)}
:host([data-ready]) .reveal{opacity:1;transform:translateY(0) scale(1)}
.tilt{position:relative;width:100%;height:100%;transform:translate(var(--px),var(--py)) rotateX(var(--rx)) rotateY(var(--ry));transition:transform .55s cubic-bezier(.2,.7,.2,1);transform-style:preserve-3d}
.float{position:relative;width:100%;height:100%;animation:cr-float var(--duration) ease-in-out infinite;animation-play-state:var(--motion)}
img{position:absolute;inset:0;width:100%;height:100%;object-fit:contain;opacity:0;transform:scale(.975);transition:opacity .8s ease,transform 1.2s cubic-bezier(.2,.7,.2,1);user-select:none;-webkit-user-drag:none;pointer-events:none}
img.visible{opacity:1;transform:scale(1)}
.shine{position:absolute;inset:0;pointer-events:none;opacity:0;mix-blend-mode:screen;mask-size:contain;mask-repeat:no-repeat;mask-position:center;-webkit-mask-size:contain;-webkit-mask-repeat:no-repeat;-webkit-mask-position:center;background:linear-gradient(112deg,transparent 44%,#fff5 49%,#fff2 51%,transparent 56%) -100% 0/260% 100% no-repeat;animation:cr-light var(--duration) ease-in-out infinite;animation-play-state:var(--motion)}
.shadow{position:absolute;bottom:9%;left:29%;width:42%;height:6%;background:radial-gradient(ellipse,#66503932,transparent 70%);border-radius:50%;filter:blur(8px);opacity:0;transition:opacity 1.2s ease;animation:cr-shadow var(--duration) ease-in-out infinite;animation-play-state:var(--motion)}
:host([data-ready]) .shadow{opacity:.75}
.fallback{position:absolute;inset:0;display:none;align-items:center;justify-content:center;text-align:center;font:14px/1.6 Arial,sans-serif;color:#8d7854;padding:24px}
:host([data-paused]) .tilt{transform:none}
:host([data-still]) .float,:host([data-still]) .shadow{animation:none}
:host([data-still]) .shine{display:none}
:host([data-still]) .reveal,:host([data-still]) .tilt,:host([data-still]) img{transition:none}
@keyframes cr-float{0%,100%{transform:translateY(2px) rotate(-.7deg)}25%{transform:translateY(-3px) rotate(.15deg)}50%{transform:translateY(-6px) rotate(.7deg)}75%{transform:translateY(-2px) rotate(.1deg)}}
@keyframes cr-light{0%,4%{background-position:-100% 0;opacity:0}8%{opacity:.25}17%{background-position:160% 0;opacity:0}100%{background-position:160% 0;opacity:0}}
@keyframes cr-shadow{0%,100%{transform:scale(1)}50%{transform:scale(.94)}}
@media(prefers-reduced-motion:reduce){.float,.shadow,.shine{animation:none}.shine{display:none}.reveal,.tilt,img{transition:none}.tilt{transform:none}}
</style><div class="shadow" aria-hidden="true"></div><div class="stage"><div class="reveal"><div class="tilt"><div class="float"><img width="1254" height="1254" decoding="async" alt=""><img width="1254" height="1254" decoding="async" alt=""><div class="shine" aria-hidden="true"></div></div></div></div></div><div class="fallback" role="status">Loading the ring…</div>`;
    this.images = [...this.root.querySelectorAll('img')];
    this.shine = this.root.querySelector('.shine');
    this.fallback = this.root.querySelector('.fallback');
    this.current = -1;
    this.currentURL = null;
    this.inView = false;
    this.nearView = false;
    this.idleStarted = false;
    this.removeAttribute('data-ready');
    this.motion = matchMedia('(prefers-reduced-motion: reduce)');
    this.pointerMedia = matchMedia('(hover: hover) and (pointer: fine)');
    this.preference = () => this.sync();
    this.motion.addEventListener('change', this.preference);
    this.pointerMedia.addEventListener('change', this.preference);
    this.visibility = () => this.sync();
    document.addEventListener('visibilitychange', this.visibility);
    this.pointerMove = event => {
      if (!this.canInteract() || event.pointerType === 'touch') return;
      this.pointerPosition = {x:event.clientX, y:event.clientY};
      if (this.pointerFrame) return;
      this.pointerFrame = requestAnimationFrame(() => {
        this.pointerFrame = null;
        if (!this.active || !this.canInteract()) return;
        const rect = this.getBoundingClientRect();
        if (!rect.width || !rect.height) return;
        const x = Math.max(-1, Math.min(1, (this.pointerPosition.x - rect.left) / rect.width * 2 - 1));
        const y = Math.max(-1, Math.min(1, (this.pointerPosition.y - rect.top) / rect.height * 2 - 1));
        this.style.setProperty('--rx', (-y * 2).toFixed(2) + 'deg');
        this.style.setProperty('--ry', (x * 3).toFixed(2) + 'deg');
        this.style.setProperty('--px', (x * 3).toFixed(2) + 'px');
        this.style.setProperty('--py', (y * 2).toFixed(2) + 'px');
      });
    };
    this.pointerLeave = () => this.resetPointer();
    this.addEventListener('pointermove', this.pointerMove);
    this.addEventListener('pointerleave', this.pointerLeave);
    if ('IntersectionObserver' in window) {
      this.nearObserver = new IntersectionObserver(entries => {
        if (!entries.some(entry => entry.isIntersecting)) return;
        this.nearView = true;
        this.nearObserver.disconnect();
        this.loadAsset();
      }, {rootMargin:'600px 0px'});
      this.nearObserver.observe(this);
      this.observer = new IntersectionObserver(entries => {
        this.inView = entries[0].isIntersecting;
        if (this.inView && !this.nearView) { this.nearView = true; this.loadAsset(); }
        this.sync();
      });
      this.observer.observe(this);
    } else {
      this.nearView = this.inView = true;
      this.loadAsset();
    }
    this.sync();
  }
  attributeChangedCallback(name) {
    if (!this.active) return;
    if (name === 'stone' || name === 'asset-base' || name.startsWith('src-')) this.loadAsset();
    this.sync();
  }
  getSelection() {
    const stone = stones.includes(this.getAttribute('stone')) ? this.getAttribute('stone') : 'sapphire';
    return {stone, metal:'silver'};
  }
  assetURL(stone) {
    const direct = this.getAttribute('src-' + stone);
    if (direct) return new URL(direct, location.href).href;
    const base = this.getAttribute('asset-base');
    return new URL(stone + '-octagon-925.webp', base ? new URL(base, location.href) : scriptBase).href;
  }
  requestAsset(url) {
    if (!this.cache.has(url)) {
      const promise = new Promise((resolve, reject) => {
        const img = new Image();
        img.decoding = 'async';
        img.fetchPriority = 'low';
        img.onload = () => resolve(img);
        img.onerror = () => reject(Error('Ring image could not load'));
        img.src = url;
      });
      this.cache.set(url, promise);
      promise.catch(() => { if (this.cache.get(url) === promise) this.cache.delete(url); });
    }
    return this.cache.get(url);
  }
  async loadAsset() {
    if (!this.active || !this.nearView) return;
    const token = ++this.token;
    const {stone, metal} = this.getSelection(), url = this.assetURL(stone);
    if (url === this.currentURL) {
      this.dispatchEvent(new CustomEvent('ring-change', {detail:{stone, metal}, bubbles:true}));
      return;
    }
    if (this.current < 0) this.fallback.style.display = 'flex';
    try {
      await this.requestAsset(url);
      if (!this.active || token !== this.token) return;
      const next = this.current === 0 ? 1 : 0;
      const front = this.images[next], old = this.current < 0 ? null : this.images[this.current];
      front.src = url;
      front.alt = names[stone] + ' octagonal step-cut ring in 925 sterling silver — generated design';
      await front.decode().catch(() => {});
      if (!this.active || token !== this.token) return;
      front.classList.add('visible');
      if (old) { old.classList.remove('visible'); old.alt = ''; }
      this.current = next;
      this.currentURL = url;
      this.shine.style.maskImage = 'url("' + url.replaceAll('"','%22') + '")';
      this.shine.style.webkitMaskImage = this.shine.style.maskImage;
      this.fallback.style.display = 'none';
      this.dispatchEvent(new CustomEvent('ring-change', {detail:{stone, metal}, bubbles:true}));
      this.sync();
      this.schedulePreload();
    } catch (error) {
      if (token !== this.token || !this.active) return;
      if (this.current < 0) { this.fallback.textContent = 'The ring image could not load. Please refresh to try again.'; this.fallback.style.display = 'flex'; }
      this.dispatchEvent(new CustomEvent('ring-error', {detail:error.message, bubbles:true}));
    }
  }
  schedulePreload() {
    if (this.idleStarted) return;
    this.idleStarted = true;
    const lifecycle = this.lifecycle;
    const preload = async () => {
      this.idleHandle = null;
      if (!this.active || lifecycle !== this.lifecycle) return;
      for (const stone of stones) {
        if (!this.active || lifecycle !== this.lifecycle) return;
        await this.requestAsset(this.assetURL(stone)).catch(() => {});
      }
    };
    this.idleKind = 'requestIdleCallback' in window ? 'idle' : 'timer';
    this.idleHandle = this.idleKind === 'idle' ? requestIdleCallback(preload, {timeout:3000}) : setTimeout(preload, 1500);
  }
  canInteract() {
    return this.active && this.current >= 0 && this.inView && this.pointerMedia.matches && !this.motion.matches && !this.hasAttribute('paused') && !document.hidden;
  }
  resetPointer() {
    if (this.pointerFrame) cancelAnimationFrame(this.pointerFrame);
    this.pointerFrame = null;
    for (const name of ['--rx', '--ry']) this.style.setProperty(name, '0deg');
    for (const name of ['--px', '--py']) this.style.setProperty(name, '0px');
  }
  sync() {
    if (!this.active) return;
    clearTimeout(this.timer);
    const reduced = this.motion.matches;
    const paused = this.hasAttribute('paused') || reduced || !this.inView || document.hidden || this.current < 0;
    const duration = Math.max(16, Math.min(45, Number(this.getAttribute('speed')) || 28));
    // 55carat addition: "dwell" is the seconds each stone shows; without it a stone shows for a quarter of the loop.
    const dwell = Math.max(2, Math.min(20, Number(this.getAttribute('dwell')) || duration / 4));
    this.toggleAttribute('data-still', reduced);
    this.toggleAttribute('data-paused', paused);
    if (this.inView && this.current >= 0) this.setAttribute('data-ready', '');
    this.style.setProperty('--duration', duration + 's');
    this.style.setProperty('--motion', paused ? 'paused' : 'running');
    if (!this.canInteract()) this.resetPointer();
    if (!paused && this.hasAttribute('cycle')) this.timer = setTimeout(() => {
      const {stone} = this.getSelection();
      this.setAttribute('stone', stones[(stones.indexOf(stone) + 1) % stones.length]);
    }, dwell * 1000);
  }
  disconnectedCallback() {
    this.active = false;
    ++this.token;
    ++this.lifecycle;
    clearTimeout(this.timer);
    if (this.idleHandle != null) {
      if (this.idleKind === 'idle') cancelIdleCallback(this.idleHandle);
      else clearTimeout(this.idleHandle);
    }
    this.resetPointer();
    this.observer?.disconnect();
    this.nearObserver?.disconnect();
    document.removeEventListener('visibilitychange', this.visibility);
    this.motion?.removeEventListener('change', this.preference);
    this.pointerMedia?.removeEventListener('change', this.preference);
    this.removeEventListener('pointermove', this.pointerMove);
    this.removeEventListener('pointerleave', this.pointerLeave);
    this.cache?.clear();
  }
}
if (!customElements.get('carat-ring')) customElements.define('carat-ring', CaratRing);
})();
